import { ChangeEvent, FC, memo, useEffect, useRef, useState } from "react";
import "../Inputs.scss";
import Eye from "../../Icons/Eye";
import ErrorMessage from "../../Tooltips/ErrorMessage";
import CustomButton from "../../Buttons/CustomButton";
import Search from "../../Icons/Search";
import { useAppSelector as useSelector } from "../../../../store";

/**
 * TextInput Props Interface
 * @interface TextInputProps
 */

interface TextInputProps {
  /** Field name/id */
  field: string;
  /** Whether field has been touched */
  touched?: boolean;
  /** Error message if validation fails */
  error?: string;
  /** Callback for value changes */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Current input value */
  value?: string;
  /** Input label */
  label?: string;
  /** Input placeholder */
  placeholder?: string;
  /** Input type (text, password, email, etc) */
  type?: string;
  /** Whether field is required */
  required?: boolean;
  /** Input ref to check the values */
  ref?: React.RefObject<HTMLInputElement | null>;
  /** Additional HTML input props */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /**  */
  defaultLabel?: string;
  /**  */
  disabled?: boolean;
  /**  */
  showSearchIcon?: boolean;
  /**  */
  floatingLabel?: boolean;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * TextInput Component
 * A reusable text input component with support for password visibility toggle,
 * error states, and accessibility features.
 */
const TextInput: FC<TextInputProps> = ({
  field,
  touched,
  error,
  onChange,
  value,
  label,
  placeholder,
  type = "text",
  required = false,
  ref,
  disabled,
  inputProps = {},
  defaultLabel,
  showSearchIcon = false,
  floatingLabel = true,
  onBlur,
  onFocus,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);

  const loading = useSelector((state) => state.app.isShow);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const isPasswordField = type === "password";
  const inputType = isPasswordField && showPassword ? "text" : type;

  useEffect(() => {
    const val = value ? value : "";
    sessionStorage.setItem(field, val);
  }, [field, value]);

  const currentLabel = defaultLabel
    ? focused || value
      ? label
      : defaultLabel
    : label;

  return (
    <div className={`form-group ${floatingLabel ? "floating-label" : ""}`}>
      {showSearchIcon && <Search />}

      <input
        type={inputType}
        className={`form-control ${touched && error ? "is-invalid" : ""}`}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        value={value}
        placeholder={placeholder}
        id={field}
        data-testid={field}
        name={field}
        ref={ref ? ref : inputRef}
        aria-describedby={error ? `${field}-error` : undefined}
        aria-invalid={touched && error ? "true" : "false"}
        aria-required={required}
        required={required}
        disabled={disabled}
        autoComplete="off"
        readOnly={loading}
        {...inputProps}
      />

      {currentLabel && (
        <label htmlFor={field} className="form-label">
          {currentLabel}
          {required && <span className="required-asterisk">*</span>}
        </label>
      )}

      {isPasswordField && (
        <CustomButton
          variant={"primary"}
          type="button"
          onClick={handleTogglePassword}
          className="password-toggle"
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? <Eye color={"#51C2B9"} /> : <Eye color={"#AAAAAA"} />}
        </CustomButton>
      )}

      {touched && error && <ErrorMessage message={error} />}
    </div>
  );
};

export default memo(TextInput);
